import styled from 'styled-components'
import { BodyLargeMedium } from '../styles/Typography'
import { theme } from '../../theme/theme'
import { useGetSymptomProgressQuery } from '../../api/ester-b2b-api/private/symptomProgress/symptomProgressQueries'
import { SymptomTrend } from '@estercare/ester-shared'
import { useTranslation } from 'react-i18next'
import { Caret } from '../icons'

const getStatusColor = (status?: SymptomTrend) => {
  switch (status) {
    case SymptomTrend.Improving:
    case SymptomTrend.Resolved:
      return theme.color.green
    case SymptomTrend.NeedsAttention:
      return theme.color.orange
    case SymptomTrend.Stable:
      return theme.color.grey3
    default:
      return theme.color.plumMid
  }
}

const getStatusBg = (status?: SymptomTrend) => {
  switch (status) {
    case SymptomTrend.Improving:
    case SymptomTrend.Resolved:
      return theme.color.greenLight
    case SymptomTrend.NeedsAttention:
      return theme.color.redLight
    default:
      return theme.color.grey2
  }
}

const getStatusTextColor = (status?: SymptomTrend) => {
  switch (status) {
    case SymptomTrend.Improving:
    case SymptomTrend.Resolved:
      return theme.color.greenDark
    case SymptomTrend.NeedsAttention:
      return theme.color.orange
    default:
      return theme.color.grey5
  }
}

const getStatusBlockBg = (status?: SymptomTrend) => {
  switch (status) {
    case SymptomTrend.Improving:
    case SymptomTrend.Resolved:
      return theme.color.greenBackground
    case SymptomTrend.NeedsAttention:
      return theme.color.redBackground
    default:
      return theme.color.plumLight
  }
}

export const SymptomProgress = ({ isFollowUpCompleted }: { isFollowUpCompleted: boolean }) => {
  const { t, i18n } = useTranslation()
  const { data } = useGetSymptomProgressQuery()
  const ratings = data?.symptomRatings?.slice(0, 5) ?? []

  const comparisonDate = data?.latestScreeningDate
    ? new Date(data.latestScreeningDate).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' })
    : null

  const getStatusLabel = (status?: SymptomTrend) => {
    switch (status) {
      case SymptomTrend.Improving:
        return t('employee.yourHealth.symptomImproving')
      case SymptomTrend.Resolved:
        return t('employee.yourHealth.symptomResolved')
      case SymptomTrend.NeedsAttention:
        return t('employee.yourHealth.symptomNeedsAttention')
      default:
        return t('employee.yourHealth.symptomStable')
    }
  }

  if (!ratings.length) return null

  return (
    <Wrapper>
      {isFollowUpCompleted && (
        <Header>
          <BodyLargeMedium>{t('employee.yourHealth.yourProgress')}</BodyLargeMedium>
          {comparisonDate && (
            <ComparisonDate>{t('employee.yourHealth.progressVs', { date: comparisonDate })}</ComparisonDate>
          )}
        </Header>
      )}
      <BlocksRow>
        {ratings.map((entry) => (
          <Block
            key={entry.category}
            $borderColor={getStatusColor(entry.status)}
            $bgColor={getStatusBlockBg(entry.status)}
          >
            {entry.previousRating !== undefined && (
              <StatusPill $bg={getStatusBg(entry.status)} $textColor={getStatusTextColor(entry.status)}>
                <PillDot $color={getStatusColor(entry.status)} />
                {getStatusLabel(entry.status)}
              </StatusPill>
            )}
            <BlockLabel>{entry.label}</BlockLabel>
            <RatingRow>
              {entry.previousRating !== undefined && (
                <>
                  <PrevRating>{entry.previousRating}</PrevRating>
                  <Arrow>→</Arrow>
                </>
              )}
              <CurrentRating $color={getStatusColor(entry.status)}>{entry.currentRating}</CurrentRating>
              <MaxValue>/{entry.maxValue}</MaxValue>
            </RatingRow>
          </Block>
        ))}
      </BlocksRow>
      {isFollowUpCompleted && (
        <LinkWrapper>
          <SeeAllLink href="/portal/your-health/health-progress">{t('employee.yourHealth.seeAllProgress')}</SeeAllLink>
          <Caret size={16} />
        </LinkWrapper>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding: ${theme.spacing.xxlarge}px ${theme.spacing.large}px ${theme.spacing.small}px ${theme.spacing.large}px;

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    padding: ${theme.spacing.large}px ${theme.spacing.small}px ${theme.spacing.medium}px;
  }
`

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${theme.spacing.medium}px;
  margin-bottom: ${theme.spacing.medium}px;
`

const ComparisonDate = styled.span`
  font-family: ${theme.font.body};
  font-size: ${theme.fontSize.small}px;
  color: ${theme.color.grey4};
  white-space: nowrap;
`

const BlocksRow = styled.div`
  display: flex;
  gap: ${theme.spacing.medium}px;
  overflow-x: auto;
  padding-bottom: ${theme.spacing.xsmall}px;

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${theme.spacing.small}px;
    padding-bottom: 0;
  }
`

const Block = styled.div<{ $borderColor: string; $bgColor: string }>`
  flex: 1;
  min-width: 150px;
  max-width: 250px;
  background: ${({ $bgColor }) => $bgColor};
  border-radius: ${theme.spacing.small}px;
  border-top: 3px solid ${({ $borderColor }) => $borderColor};
  padding: ${theme.spacing.medium}px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xsmall}px;
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.06),
    0px 3px 9px 0px rgba(0, 0, 0, 0.05);
`

const StatusPill = styled.div<{ $bg: string; $textColor: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xsmall}px;
  background-color: ${({ $bg }) => $bg};
  color: ${({ $textColor }) => $textColor};
  border-radius: 999px;
  padding: 4px 10px;
  font-family: ${theme.font.body};
  font-size: ${theme.fontSize.xsmall}px;
  font-weight: 500;
  width: fit-content;
`

const PillDot = styled.span<{ $color: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`

const BlockLabel = styled.p`
  font-family: ${theme.font.heading};
  font-size: ${theme.fontSize.default}px;
  line-height: 1.2;
  color: ${theme.color.black};
  margin-top: ${theme.spacing.xsmall}px;
`

const RatingRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-top: auto;
  padding-top: ${theme.spacing.small}px;
`

const PrevRating = styled.span`
  font-family: ${theme.font.body};
  font-size: ${theme.fontSize.medium}px;
  color: ${theme.color.grey4};
`

const Arrow = styled.span`
  font-family: ${theme.font.body};
  font-size: ${theme.fontSize.default}px;
  color: ${theme.color.grey4};
`

const CurrentRating = styled.span<{ $color: string }>`
  font-family: ${theme.font.heading};
  font-size: ${theme.fontSize.large}px;
  font-weight: 500;
  color: ${({ $color }) => $color};
  line-height: 1;
`

const MaxValue = styled.span`
  font-family: ${theme.font.body};
  font-size: ${theme.fontSize.small}px;
  color: ${theme.color.grey4};
`

const SeeAllLink = styled.a`
  font-family: ${theme.font.body};
  font-size: ${theme.fontSize.small}px;
  text-decoration: underline;
  cursor: pointer;
`

const LinkWrapper = styled.div`
  margin-top: ${theme.spacing.medium}px;
  color: ${theme.color.black};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.tiny}px;
`
